import styled from "styled-components";
import { ReactComponent as CrossIcon } from "../../../assets/cross_icon.svg";
import { BASIC_LINE, BASIC_WHITE } from "../../../constants/colors";
import { AppliedCoupon } from "../../../ducks/coupon";
import { Menu, MenuInCart } from "../../../ducks/menu";
import { OrderCoupon, OrderMenu } from "../../../ducks/order";
import { CouponInput } from "../../molecules/menu/CouponInput";
import { OrderContent } from "../order/OrderContent";
import { OrderItem } from "../order/OrderItem";

type Props = {
  isShow: boolean;
  orderedMenus: OrderMenu[];
  selectedLimitedMenus: Menu[];
  menusInCart: MenuInCart[];
  menuMapById: Map<number, Menu>;
  shouldAnimate: boolean;
  showCartOrderContent: boolean;
  totalPriceInCart: number;
  totalItemsInCart: number;
  totalTaxInCart: number;
  orderCoupon?: OrderCoupon;
  handleClickCartOrderContent: () => void;
  handleCloseCartPopup: () => void;
  handleFavorite: (menuId: number, menuCode: string, favorite: boolean) => void;
  handleUpdateItem: (
    index: number,
    amount: number,
    optionId?: number,
    optionName?: string,
  ) => void;
  handleRemoveItem: (index: number) => void;
  onRemoveCoupon: (couponUuid: string, isAppCoupon: boolean) => void;
  getCouponName: (couponCode: string) => string;
  appliedCoupons: AppliedCoupon[] | null;
};

export const CartDetailPopup: React.FC<Props> = ({
  isShow,
  orderedMenus,
  selectedLimitedMenus,
  menusInCart,
  menuMapById,
  shouldAnimate,
  showCartOrderContent,
  totalPriceInCart,
  totalItemsInCart,
  totalTaxInCart,
  orderCoupon,
  handleClickCartOrderContent,
  handleCloseCartPopup,
  handleFavorite,
  handleUpdateItem,
  handleRemoveItem,
  onRemoveCoupon,
  getCouponName,
  appliedCoupons,
}) => {
  const getUniqueKey = (menuId: number, optionId?: number) =>
    `${Math.random().toString(36).slice(-8)}-${menuId}-${optionId ?? "none"}`;

  return (
    <CartModal $active={isShow}>
      <CartModalBackground $active={isShow} onClick={handleCloseCartPopup} />
      <CartModalBoard $active={isShow}>
        <CartModalWrapper>
          <CartModalItemSection>
            <CartModalHeader>
              <CartModalHeaderTitle>カート</CartModalHeaderTitle>

              <CartModalHeaderAction onClick={handleCloseCartPopup}>
                <CrossIcon />
              </CartModalHeaderAction>
            </CartModalHeader>

            <CouponInput
              showInput={false}
              onUseAppCoupon={() => {}}
              onApplyCoupon={() => {}}
              onRemoveCoupon={onRemoveCoupon}
              getCouponName={getCouponName}
              appliedCoupons={appliedCoupons}
            />

            {menusInCart.map((menuInCart, index) => {
              const menu = menuMapById.get(menuInCart.menuId);

              if (!menu) return <div key={index}></div>;

              return (
                <OrderItem
                  key={getUniqueKey(menu.id, menuInCart.optionId)}
                  menu={menu}
                  menuInCart={menuInCart}
                  selectedLimitedMenus={selectedLimitedMenus}
                  animate={shouldAnimate}
                  onFavorite={() =>
                    handleFavorite(menu.id, menu.code, !menu.isFavorite)
                  }
                  onUpdateItem={(...args) => handleUpdateItem(index, ...args)}
                  onRemove={() => handleRemoveItem(index)}
                />
              );
            })}
          </CartModalItemSection>

          <OrderContentSection>
            <OrderContent
              menus={orderedMenus}
              totalPrice={totalPriceInCart}
              totalQuantity={totalItemsInCart}
              taxAmount={totalTaxInCart}
              coupon={orderCoupon}
              hideMemo
              showCloseButton
              showCartOrderContent={showCartOrderContent}
              onClose={handleClickCartOrderContent}
              fontSize="20px"
            />
          </OrderContentSection>
        </CartModalWrapper>
      </CartModalBoard>
    </CartModal>
  );
};

const CartModal = styled.div<{ $active: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  height: 100%;
  overflow: scroll;
  transform: translate(0, ${({ $active }) => ($active ? "0" : "100%")});
  transition-delay: ${({ $active }) => ($active ? "0s" : "0.25s")};
`;

const CartModalBackground = styled.div<{ $active: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  cursor: pointer;
  opacity: ${({ $active }) => ($active ? "1" : "0")};
  transition: all 0.5s linear;
`;

const CartModalBoard = styled.div<{ $active: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: calc(100% - 84px);
  background-color: ${BASIC_WHITE};
  overflow-y: auto;
  opacity: ${({ $active }) => ($active ? "1" : "0")};
  transition: all 0.5s cubic-bezier(0.13, 0.79, 0.36, 0.97);
  transform: translate(0, ${({ $active }) => ($active ? "0" : "100%")});
`;

const CartModalWrapper = styled.div`
  max-width: 980px;
  position: relative;
  margin: auto;
  display: flex;
  flex-direction: column;
  padding: 36px 16px 0;
`;

const CartModalItemSection = styled.div`
  display: flex;
  padding: 0;
  flex-direction: column;
`;

const CartModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  padding: 0 0 9px;
  margin: 0 0 30px;
  line-height: 1;
  border-bottom: 1px solid ${BASIC_LINE};
`;

const CartModalHeaderTitle = styled.p`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  padding: 0;
`;

const CartModalHeaderAction = styled.div`
  width: 25px;
  height: 25px;
  cursor: pointer;
  margin: 0;
  user-select: none;
`;

const OrderContentSection = styled.div`
  display: flex;
  flex-direction: column;
  margin: 60px 0 114px 0;
`;
